import Vue from 'vue'

import { axiosRequest } from '@/plugins/axios-common'
import { handleError } from '@/store/handle-error'

import { LEGAL_CASE_NAMESPACE } from '@/store/legal-case'

import { sortCharacteristicCards } from './party-edit/helpers/card-sort'

import {
  AsylumAssystantSectionActions,
  AsylumAssystantSectionGetters,
  AsylumAssystantSectionMutations,
} from './party-edit/asylum-assystant-section'
import {
  BackgroundSectionActions,
  BackgroundSectionGetters,
  BackgroundSectionMutations,
} from './party-edit/background-section'
import {
  FamiliesSectionActions,
  FamiliesSectionGetters,
  FamiliesSectionMutations,
} from './party-edit/families-section'
import {
  HistorySectionActions,
  HistorySectionGetters,
  HistorySectionMutations,
} from './party-edit/history-section'
import {
  OverviewSectionActions,
  OverviewSectionGetters,
  OverviewSectionMutations,
  OverviewSectionState,
} from './party-edit/overview-section'
import {
  PESharedActions,
  PESharedGetters,
  PESharedMutations,
} from './party-edit/shared'
import {
  PersonalDataSectionActions,
  PersonalDataSectionGetters,
  PersonalDataSectionMutations,
} from './party-edit/personal-data-section'
import {
  SurveysSectionActions,
  SurveysSectionGetters,
  SurveysSectionMutations,
  SurveysSectionState,
} from './party-edit/surveys-section'
import {
  fullBooleanReducer,
  fullPartySectionSetter,
  processPartyEditRequirements,
} from './party-edit/helpers/party-edit'
import { PESharedQuickEditState } from './party-edit/shared/pe-shared-quick-edit'

const virginPartyEdit = {
  partyPermissions: {
    access: false,
    update: false,
  },
  party: {},
  party_survey_session: null,
  partyDocuments: [],
  photoBlob: null,
  photoLoading: false,
  photoOriginalBlob: null,
  asylumAssystant: {},
  partyLoading: false,
  genericSectionLoading: true,
  quickEdit: PESharedQuickEditState,
  partySections: {},
  surveys: SurveysSectionState,
  overview: OverviewSectionState,
  cardsOthersUpdated: {},
  pendingCardScrollIdentifier: null,
}
export const PartyEditState = {
  partyEdit: { ...virginPartyEdit },
}

export const PartyEditActions = {
  peGetPartySection: async ({ commit, dispatch }, { id }) => {
    commit('peSetPartyLoading', true)
    commit('peSetGenericSectionLoading', true)
    return dispatch('peCheckPartyPermissions', { id }).
      then(_ => axiosRequest.
        get(`/parties/${id}`).
        then(({ data }) => {
          commit('peSetParty', processPartyEditRequirements(data?.party))
          commit('peSetPartyLoading', false)
          commit('peSetGenericSectionLoading', false)
          dispatch('peProcessPhoto')
          dispatch('peFetchPartyDocuments')
        }).
        catch(handleError))
  },

  loadPartyForQuickInterview: async ({ commit, dispatch }, { id }) => {
    return dispatch('peCheckPartyPermissions', { id }).
      then(_ => axiosRequest.
        get(`/parties/${id}`).
        then(({ data }) => {
          commit('peSetParty', processPartyEditRequirements(data?.party))
          dispatch('peProcessPhoto')
          dispatch('peFetchPartyDocuments')
        }).
        catch(handleError))
  },

  peGetSelectedPartySection: async ({ commit, state, dispatch }, { id, section }) => {
    commit('peSetPartyLoading', true)
    commit('peSetGenericSectionLoading', true)
    return dispatch('peCheckPartyPermissions', { id }).
      then(_ => axiosRequest.
        get(`/parties/${id}/sections/${section}`).
        then(({ data }) => {
          commit('peSetParty', processPartyEditRequirements({
            ...state.partyEdit.party,
            ...data.party,
          }))
          commit('peSetPartyLoading', false)
          commit('peSetGenericSectionLoading', false)
          dispatch('peProcessPhoto')
          dispatch('peFetchPartyDocuments')
        }).
        catch(handleError))
  },
  peCheckPartyPermissions: async ({ commit }, { id }) => {
    return axiosRequest.
      get(`/parties/${id}/permissions`).
      then(({ data }) => {
        commit('peSetPartyPermissions', data)
      }).
      catch(handleError)
  },
  peLockPartySections: ({ commit, state }) => {
    const partySections = state.partyEdit.partySections
    commit('peSetPartySections', fullPartySectionSetter(partySections, true))
  },
  peResetPartyEdit: ({ commit }) => commit('peSetPartyEdit', { ...virginPartyEdit }),
  pePartyOtherUpdated: ({ commit }, { value }) => {
    commit('peSetPartyOtherUpdated', { value, identifier: 'party-details-card' })
    commit('peSetPartyOtherUpdated', { value, identifier: 'identifiers-card' })
    commit('peSetPartyOtherUpdated', { value, identifier: 'demographics-card' })
    commit('peSetPartyOtherUpdated', { value, identifier: 'physical-address-card' })
    commit('peSetPartyOtherUpdated', { value, identifier: 'mailing-address-card' })
    commit('peSetPartyOtherUpdated', { value, identifier: 'income-card' })
  },
  pePartySetOthersUpdated: ({ commit, dispatch }, { identifier, value }) => {
    const cardId = identifier.split('-card-')

    if (cardId.length > 1) commit('peSetPartyOtherUpdated', { value, identifier })
    else dispatch('pePartyOtherUpdated', { value })
  },
  pePartyCharacteristicOthersCreated: ({ commit }, { characteristic, value }) => {
    commit('pePatchCharacteristic', { characteristic, value })
  },
  pePartyCharacteristicOthersDeleted: ({ commit }, { characteristic, id }) => {
    commit('pePatchCharacteristicDelete', { characteristic, id })
  },
  peSetCollapsedAllCards: _ => {},
  peSetCollapsedCardsByIdentifier: _ => {},
  peSetPendingCardScrollIdentifier: ({ commit }, identifier) => commit('peSetPendingCardScrollIdentifier', identifier),
  ...AsylumAssystantSectionActions,
  ...PersonalDataSectionActions,
  ...FamiliesSectionActions,
  ...BackgroundSectionActions,
  ...OverviewSectionActions,
  ...HistorySectionActions,
  ...PESharedActions,
  ...SurveysSectionActions,
}

export const PartyEditGetters = {
  genericSectionLoading: state => state.partyEdit.genericSectionLoading,
  pePartyLoading: state => state.partyEdit.partyLoading,
  peGenericSectionLoading: state => state.partyEdit.genericSectionLoading,
  peParty: state => state.partyEdit.party || {},
  peAnyUnlockedSections: state => !fullBooleanReducer(state.partyEdit.partySections),
  peUserCanUpdate: state => state.partyEdit.partyPermissions.update,
  peUserCanAccess: state => state.partyEdit.partyPermissions.access,
  pePartyCardsOtherUpdated: state => {
    return state.partyEdit?.cardsOthersUpdated || {}
  },
  pePendingCardScrollIdentifier: state => state.partyEdit?.pendingCardScrollIdentifier || null,
  peSortedCharacteristic: state => characteristic => sortCharacteristicCards(characteristic, state.partyEdit.party[characteristic]),
  ...AsylumAssystantSectionGetters,
  ...PersonalDataSectionGetters,
  ...FamiliesSectionGetters,
  ...BackgroundSectionGetters,
  ...OverviewSectionGetters,
  ...HistorySectionGetters,
  ...PESharedGetters,
  ...SurveysSectionGetters,
}

export const PartyEditMutations = {
  peSetPartyEdit: (state, partyEdit) => { state.partyEdit = partyEdit },
  peSetParty: (state, party) => { state.partyEdit.party = party },
  pePatchParty: (state, patch) => {
    for (const key of Object.keys(patch)) {
      Vue.set(state.partyEdit.party, key, patch[key])
    }

    // TODO: This really shouldn't be here .. these calls should be made from actions, mutations should never call other
    // mutations. But all the Party Edit actions that partially update the Party data call this mutation in order to
    // persist the changes, and it would be extremely cumbersome to update dozens of actions for each to call the
    // following two mutations, so the Party data is also updated on Records and Legal Cases.
    //
    // Ideally there should be a central Party Store that the Record and Legal Case stores would reference to fetch the
    // Parties data they need and that Party Edit would commit the changes made to a Party.
    //
    // This is truly a hack, and a store refactoring will need to happen to get rid of this.

    const store = require('@/store').default
    store.commit('updateExistingRecordPartyModel', state.partyEdit.party, { root: true })
    store.commit(`${LEGAL_CASE_NAMESPACE}/updateLegalCaseParty`, state.partyEdit.party, { root: true })
  },
  peSetPartyLoading: (state, value) => { state.partyEdit.partyLoading = value },
  peSetGenericSectionLoading: (state, value) => { state.partyEdit.genericSectionLoading = value },
  peSetPartySections: (state, value) => { state.partyEdit.partySections = value },
  pePatchCharacteristic: (state, { characteristic, value }) => {
    let index = state.partyEdit.party[characteristic]?.findIndex(i => i.id === value.id)

    if (index === -1) index = state.partyEdit.party[characteristic]?.length || 0

    Vue.set(state.partyEdit.party[characteristic], index, value)
  },
  pePatchCharacteristicNew: (state, { characteristic, value }) => {
    let index = state.partyEdit.party[characteristic]?.findIndex(i => i.id === null)

    if (index === -1) index = state.partyEdit.party[characteristic]?.length || 0

    Vue.set(state.partyEdit.party[characteristic], index, value)
  },
  pePatchCharacteristicDelete: (state, { characteristic, id }) => {
    const index = state.partyEdit.party[characteristic]?.findIndex(i => i.id === id)

    if (index !== -1) state.partyEdit.party[characteristic].splice(index, 1)
  },
  peSetPartyPermissions: (state, { update, access }) => {
    Vue.set(state.partyEdit.partyPermissions, 'access', access)
    Vue.set(state.partyEdit.partyPermissions, 'update', update)
  },

  peSetPartyOtherUpdated: (state, { value, identifier }) => {
    Vue.set(state.partyEdit.cardsOthersUpdated, identifier, value)
  },

  peSetPendingCardScrollIdentifier: (state, identifier) => {
    state.partyEdit.pendingCardScrollIdentifier = identifier
  },

  ...AsylumAssystantSectionMutations,
  ...PersonalDataSectionMutations,
  ...FamiliesSectionMutations,
  ...BackgroundSectionMutations,
  ...OverviewSectionMutations,
  ...HistorySectionMutations,
  ...PESharedMutations,
  ...SurveysSectionMutations,
}
